import { fg } from '@atlaskit/platform-feature-flags';
import { ROVO_SKILL_TAG_PATTERN } from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useSkillMetadata } from '@atlassian/rovo-skills/services/skill-metadata-provider';

import { type FilterOption } from '../../common/types';

const PROMPT_CATEGORIES = [
	'Analyze',
	'Brainstorm',
	'Review',
	'Summarize',
	'Create',
	'Execute',
	'Find',
	'Learn',
] as const;

const PROMPT_ROLES = [
	'General',
	'Engineering',
	'Product',
	'Program Management',
	'Design',
	'Marketing',
	'IT Ops',
	'Sales',
	'Customer Support',
	'Helpdesk Support',
	'Security',
] as const;

export type PromptCategory = (typeof PROMPT_CATEGORIES)[number];
export type PromptRole = (typeof PROMPT_ROLES)[number];

const createFilterOptions = <T extends readonly string[]>(values: T): FilterOption[] =>
	values.map((value) => ({ id: value, label: value }));

export const useCaseOptions: FilterOption[] = createFilterOptions(PROMPT_CATEGORIES);
export const roleOptions: FilterOption[] = createFilterOptions(PROMPT_ROLES);

export interface PromptCard {
	id: string;
	title: string;
	prompt: string;
	useCase?: PromptCategory;
	roles?: PromptRole[];
	enableDeepResearch?: boolean;
	/** App/source IDs assigned when creating the prompt (e.g. 'jira', 'confluence') */
	appIds?: string[];
	/** Optional skill registry tags. The tag 'rovo-chat-prompt-gallery' is always applied for these cards. */
	tags?: string[];
	/** Whether the prompt is favorited by the user */
	isFavorited?: boolean;
	/** Share ID from the API - only present for prompts fetched from the GET prompts API */
	shareId?: string;
	/** Whether this prompt was created by the user (from the GET prompts API) */
	isUserGenerated?: boolean;
}

export const PROMPT_LIBRARY_SKILL_TAG = 'rovo-chat-prompt-gallery';

// Pattern for prompt-library skill tag syntax: '/slug' (quoted).
const PROMPT_LIBRARY_SKILL_TAG_PATTERN_TEST = /'\/[a-zA-Z0-9-]+'/;
// Global pattern with capturing group for extracting all slugs (exec() advances with /g).
const PROMPT_LIBRARY_SKILL_TAG_PATTERN_GLOBAL = /'\/([a-zA-Z0-9-]+)'/g;
export const promptContainsSkillTagSyntax = (prompt: string): boolean =>
	PROMPT_LIBRARY_SKILL_TAG_PATTERN_TEST.test(prompt);

export const getSkillSlugsInPrompt = (prompt: string): string[] => {
	const slugs: string[] = [];
	if (fg('rovo_chat_encode_adf_with_skills')) {
		const slashSkillPattern = new RegExp(ROVO_SKILL_TAG_PATTERN.source, 'g');
		for (const m of prompt.matchAll(slashSkillPattern)) {
			slugs.push(m[1]);
		}
	} else {
		let match: RegExpExecArray | null;
		PROMPT_LIBRARY_SKILL_TAG_PATTERN_GLOBAL.lastIndex = 0;
		while ((match = PROMPT_LIBRARY_SKILL_TAG_PATTERN_GLOBAL.exec(prompt)) !== null) {
			slugs.push(match[1]);
		}
	}
	return slugs;
};

// Prompts with multiple placeholders are commented out until they are supported by this
// PR https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/217105
const staticPrompts: PromptCard[] = [
	{
		id: 'analyze-customer-feedback-trends',
		title: 'Analyze customer feedback trends',
		prompt:
			'Gather and synthesize customer feedback on `product/feature` from various channels such as emails, calls, surveys, support tickets to identify key themes, pain points, opportunities such as upsell and adoption, and organize findings into actionable recommendations. If relevant, map them to stages of the customer journey.',
		useCase: 'Analyze',
		roles: [
			'Product',
			'Marketing',
			'Engineering',
			'Program Management',
			'Customer Support',
			'Sales',
		],
		enableDeepResearch: true,
	},
	// {
	// 	id: 'analyze-it-support-trends',
	// 	title: 'Analyze Trends in IT Support Requests',
	// 	prompt:
	// 		'Examine all IT support requests related to `request type` from the past `number of months` months. Break down the request type by identifying the most common sub-categories and providing counts for each. Investigate resolution notes or agent comments to uncover recurring root causes behind these issues. Identify any time-based patterns, such as spikes in requests during specific times of day, days of the week, or months. Based on your findings, recommend actionable strategies to effectively reduce the volume of this request type.',
	// 	useCase: 'Analyze',
	// 	roles: ['Helpdesk Support', 'IT Ops', 'Customer Support', 'Program Management'],
	// },
	{
		id: 'brainstorm-project-ideas',
		title: 'Brainstorm ideas for project',
		prompt:
			"We're running a brainstorming session on `topic/problem/goal`. Generate a diverse list of creative ideas, solutions, and/or approaches. For each idea, include a brief description, potential benefits, and any risks or challenges. Organize the ideas by theme or category if possible.",
		useCase: 'Brainstorm',
		roles: ['General'],
	},
	{
		id: 'send-slack-weekly-progress',
		title: 'Send Slack message of weekly progress',
		prompt:
			'Draft a summary of the key updates and additions I made over the past week. Send this summary in Slack to `Teams channel link`. Confirm the draft with me before sending.',
		useCase: 'Execute',
		roles: ['General'],
	},
	{
		id: 'compare-jira-support-article',
		title: 'Compare Jira work item to support article',
		prompt:
			'Review the summary, description, and comments of the current request `work item ID`. Based on this information and considering common themes from recently resolved requests, determine if this issue points to a recurring problem that currently lacks a dedicated knowledge base article. If so, propose a concise title for a new knowledge base article and 2-3 key topics or resolution steps it should cover. This should be a distinct problem, not a minor variation of an existing knowledge base article.',
		useCase: 'Review',
		roles: ['Helpdesk Support', 'Customer Support', 'IT Ops', 'Program Management'],
	},
	// {
	// 	id: 'conduct-competitive-research',
	// 	title: 'Conduct competitive research',
	// 	prompt:
	// 		'Analyze the competitive landscape for `product` against `Competitor A, B, C, and D` by comparing features, pricing, market positioning, and strengths. Identify market gaps for `product` to explore. Summarize findings in a narrative, including a comparison matrix.',
	// 	useCase: 'Analyze',
	// 	roles: ['Product', 'Program Management', 'Marketing', 'Sales'],
	// },
	{
		id: 'convert-request-jql-query',
		title: 'Convert request into JQL query',
		prompt:
			'Write a JQL query to find all unresolved bugs assigned to my team in the last 30 days.',
		useCase: 'Execute',
		roles: ['Engineering', 'Product', 'IT Ops'],
	},
	// {
	// 	id: 'create-jira-work-item-summaries',
	// 	title: 'Create a document of Jira work item summaries',
	// 	prompt:
	// 		'Generate a Confluence page summarizing information in these work items: `work item ID(s)`.',
	// 	useCase: 'Summarize',
	// 	roles: ['Product', 'Program Management', 'Engineering', 'Customer Support'],
	// },
	// {
	// 	id: 'create-new-sprint',
	// 	title: 'Create a new sprint',
	// 	prompt:
	// 		'Create a new sprint named `sprint name` on the board: `board key`. Set the sprint goal to `sprint goal`, start date to `YYYY-MM-DD`, and end date to `YYYY-MM-DD`.',
	// 	useCase: 'Execute',
	// 	roles: ['Engineering', 'Product', 'Program Management'],
	// },
	{
		id: 'create-ab-testing-plan',
		title: 'Create A/B testing plan',
		prompt:
			'Create a detailed A/B testing plan for `project/feature`. Start by defining the hypothesis and primary goal, specifying the variants to be tested, and identifying the key metric(s) for success. Outline the target audience and sample size, describe how users will be randomly assigned to each group, and set the test duration and timeline. Include steps for implementation, data collection, and analysis, and note any tools or platforms to be used. Finally, add placeholders for documenting results, learnings, and next steps, ensuring the plan is clear, actionable, and easy to adapt for different experiments, with space for users to add or modify details as needed.',
		useCase: 'Create',
		roles: ['Product', 'Marketing', 'Engineering', 'Design'],
	},
	// {
	// 	id: 'create-effective-okr',
	// 	title: 'Create an effective OKR',
	// 	prompt: 'Help me create an effective OKR for my team.',
	// 	useCase: 'Create',
	// 	roles: ['General', 'Product', 'Program Management'],
	// },
	{
		id: 'create-background-research-plan',
		title: 'Create background research plan',
		prompt:
			'Create a first draft or conduct background research on `topic/work item`. Summarise key points, relevant history, and provide links to supporting documents.',
		useCase: 'Summarize',
		roles: ['Product', 'Program Management', 'Engineering', 'Design'],
	},
	{
		id: 'create-customer-onboarding-strategy',
		title: 'Create customer onboarding strategy',
		prompt:
			'As a customer success lead, outline a clear customer onboarding strategy for `product or service`, including key objectives, main onboarding steps, communication touchpoints, and success metrics; highlight best practices for activation and engagement, and provide space for users to add project-specific details or adapt the strategy for different customer segments, ensuring the plan is concise, actionable, and easy to share with stakeholders.',
		useCase: 'Create',
		roles: ['Customer Support', 'Sales', 'Product', 'Program Management'],
	},
	// {
	// 	id: 'create-jira-work-item',
	// 	title: 'Create Jira work item',
	// 	prompt:
	// 		'Create a Jira work item in project `project key` with work type as `bug/task/story/epic`, status as TO DO, classification level as project default, and the summary "`summary`."',
	// 	useCase: 'Execute',
	// 	roles: ['Engineering', 'Product', 'IT Ops', 'Customer Support'],
	// },
	// {
	// 	id: 'create-meeting-agenda',
	// 	title: 'Create meeting agenda',
	// 	prompt:
	// 		'Create a `number of minutes/hours` meeting agenda with `person/team/department` on `meeting topic` to discuss `objectives for the meeting`. Based on these objectives, suggest key topics, allocate appropriate time for each, and list any supporting materials needed.',
	// 	useCase: 'Create',
	// 	roles: ['General'],
	// },
	// {
	// 	id: 'create-personalized-onboarding-plan',
	// 	title: 'Create personalized onboarding plan',
	// 	prompt:
	// 		'Create a 30-day plan to help me get up to speed. I am onboarding onto `team` as a `role`. Include learning resources, team priorities, key contacts, and engagement strategies.',
	// 	useCase: 'Create',
	// 	roles: ['General'],
	// },
	// {
	// 	id: 'create-presentation-outline',
	// 	title: 'Create presentation outline',
	// 	prompt:
	// 		'Outline a talk track for a presentation targeting `company/product` clients in the `sector` to educate them on the `solution`. Ensure the talk track is clear, informative, and formatted with timestamps in a table.',
	// 	useCase: 'Create',
	// 	roles: ['Product', 'Marketing', 'Sales'],
	// },
	// {
	// 	id: 'create-pricing-model-customers',
	// 	title: 'Create pricing model for customers',
	// 	prompt:
	// 		'Generate a pricing model for `customer/product` using the latest customer data. Include scenario analysis and pricing plan comparisons.',
	// 	useCase: 'Create',
	// 	roles: ['Sales', 'Product', 'Marketing'],
	// },
	{
		id: 'create-product-requirements-document',
		title: 'Create product requirements document',
		prompt:
			'Draft a PRD for `project/feature`. Depending on context such as OKRs, PRD, A/B test, retrospective, include objectives, hypotheses, requirements, metrics, learnings, and next steps.',
		useCase: 'Create',
		roles: ['Program Management', 'Design', 'Product', 'Engineering'],
	},
	// {
	// 	id: 'create-recurring-calendar-meeting',
	// 	title: 'Create recurring calendar meeting',
	// 	prompt:
	// 		'Create a `number of minutes` recurring calendar meeting every `day` at `HH:MM am/pm` `timezone` for `project` with `person`.',
	// 	useCase: 'Execute',
	// 	roles: ['General'],
	// },
	// {
	// 	id: 'create-release-notes',
	// 	title: 'Create release notes',
	// 	prompt:
	// 		'Create clear release documentation for `project/product`. Include release date, features, fixes, known issues, and Jira work items/references if found. Provide output in multiple formats such as Confluence, PDF, Markdown as needed.',
	// 	useCase: 'Create',
	// 	roles: ['Engineering', 'Program Management', 'Product', 'Customer Support'],
	// },
	// {
	// 	id: 'create-resolution-notes',
	// 	title: 'Create resolution notes',
	// 	prompt:
	// 		'Create resolution notes for future reference suitable for other agents and team leads to quickly understand the following work item and its resolution: `work item link`. Include a brief and clear description of the problem, the identified root cause, and a summary of the diagnostic steps and actions performed. Clearly state the exact resolution that was implemented. Specify the total time from ticket creation to resolution using HH:MM format, or days/hours if longer.',
	// 	useCase: 'Create',
	// 	roles: ['Helpdesk Support', 'Customer Support', 'IT Ops'],
	// },
	// {
	// 	id: 'draft-blog-post',
	// 	title: 'Draft a blog post',
	// 	prompt:
	// 		'Draft a blog post or marketing collateral for `product/feature`. Highlight key benefits, use cases, and include customer testimonials if available.',
	// 	useCase: 'Create',
	// 	roles: ['Marketing', 'Product', 'Sales'],
	// },
	// {
	// 	id: 'draft-usability-testing-script',
	// 	title: 'Draft a usability testing script',
	// 	prompt:
	// 		'Draft a detailed usability testing script for the following feature/product `Confluence page link`. The script should be suitable for a moderated session and include the following sections: Introduction for the participant (Reassure the participant that the focus is on the product, not their performance), Warm-up/background questions, Scenario setup, Task instructions, and Wrap-up questions. For each task, specify what a successful outcome looks like and, if appropriate, include a rating scale for ease of use. Throughout, use accessible, concise, and clear language, avoid jargon and directional UI language, keep instructions and questions short and focused, and ensure the script is easy to follow for both moderator and participant, formatting it as a template that can be easily adapted for different products or features',
	// 	useCase: 'Create',
	// 	roles: ['Design', 'Product'],
	// },
	// {
	// 	id: 'draft-and-send-email',
	// 	title: 'Draft and send email',
	// 	prompt:
	// 		"Draft an email to `email address` with the subject `Subject` with a message regarding `topic/purpose`. Once I've confirmed, send this email.",
	// 	useCase: 'Execute',
	// 	roles: ['General'],
	// },
	{
		id: 'edit-confluence-page-status',
		title: 'Edit Confluence page status',
		prompt: "Update this page's status to Ready for Review: `Confluence page link`",
		useCase: 'Execute',
		roles: ['General', 'Engineering', 'Product', 'Program Management'],
	},
	// {
	// 	id: 'explain-acronym',
	// 	title: 'Explain an acronym',
	// 	prompt:
	// 		"Explain the meaning of the acronym '`acronym`' in this context: `context`. Provide its full form and a brief definition.",
	// 	useCase: 'Learn',
	// 	roles: ['General'],
	// },
	// {
	// 	id: 'explain-end-to-end-service-flow',
	// 	title: 'Explain End-to-End Service Flow',
	// 	prompt:
	// 		'Explain the `process/system flow` for `feature` as experienced by `end-user/customer`. Include each step from `start event` to `end event`. Highlight any system interactions, notifications, and common issues or exceptions. Provide diagrams or documentation links if available.',
	// 	useCase: 'Learn',
	// 	roles: [
	// 		'Sales',
	// 		'IT Ops',
	// 		'Program Management',
	// 		'Customer Support',
	// 		'Product',
	// 		'Security',
	// 		'Helpdesk Support',
	// 		'Engineering',
	// 	],
	// },
	{
		id: 'explain-code',
		title: 'Explain this code',
		prompt:
			'Explain what the code below does, describing its purpose, how it works, and any important details or logic involved. Use clear, concise language suitable for someone with basic programming knowledge. `Insert code snippet`.',
		useCase: 'Learn',
		roles: ['Engineering'],
	},
	{
		id: 'extract-key-insights-datasets',
		title: 'Extract key insights from datasets',
		prompt:
			'`Upload dataset file(s)` Based on the provided dataset, please analyze and present the top 5 most interesting observations, highlights, or trends, identifying segments that were more likely to respond in a certain way, significant patterns, or unexpected insights from the data.',
		useCase: 'Analyze',
		roles: ['Product', 'Marketing', 'Engineering'],
	},
	// {
	// 	id: 'find-analyze-customer-contracts',
	// 	title: 'Find and analyze information from customer contracts',
	// 	prompt:
	// 		'Analyze all existing contracts for `project/customer segment/time period` and extract information on response times, maintenance obligations, and product rates. Present findings in a comparative table, highlighting any variations between customers. If available, include contract names (`Contract name/ID`), customer names (`Customer A, Customer B]), and any relevant contract dates (`Effective Date, Expiry Date`).',
	// 	useCase: 'Analyze',
	// 	roles: ['Product', 'Program Management'],
	// },
	{
		id: 'find-prioritize-open-pull-requests',
		title: 'Find and prioritize open pull requests',
		prompt:
			"Summarize all my open pull requests in across all repositories I have access to in Bitbucket, as well as all pull requests where I'm assigned as a reviewer. Clearly indicate the number of pull requests pending my review and the total number of open PRs involving me. Group the results by repository and include key details such as PR title, status, author, and last updated date.",
		useCase: 'Summarize',
		roles: ['Engineering'],
	},
	// {
	// 	id: 'find-hr-policies',
	// 	title: 'Find HR policies',
	// 	prompt:
	// 		'Find all the HR policies that a new hire joining the `location` office would need during onboarding. Reference the latest HR policy resources. Organize results in a table and check if all required policies and contacts are included.',
	// 	useCase: 'Find',
	// 	roles: ['General'],
	// },
	// {
	// 	id: 'find-information-3p-sources',
	// 	title: 'Find information from 3P sources',
	// 	prompt: 'Find the latest `topic` presentation in our Google Drive or other 3rd party sources.',
	// 	useCase: 'Find',
	// 	roles: ['Product', 'Marketing', 'Sales'],
	// },
	{
		id: 'find-overdue-jira-work-items',
		title: 'Find overdue Jira work items for my team',
		prompt:
			'Show all overdue work items for my team, including links to them and how long they have been overdue.',
		useCase: 'Find',
		roles: ['Engineering', 'IT Ops', 'Helpdesk Support', 'Program Management'],
	},
	// {
	// 	id: 'find-potential-security-vulnerabilities',
	// 	title: 'Find potential security vulnerabilities',
	// 	prompt:
	// 		'For this pull request `pull request ID`, identify and summarize any changes that were made that have potential security vulnerabilities.',
	// 	useCase: 'Find',
	// 	roles: ['Security', 'IT Ops'],
	// },
	// {
	// 	id: 'find-related-jira-projects',
	// 	title: 'Find related Jira projects',
	// 	prompt: 'What Jira projects have ongoing work that relates to this decision: `description`?',
	// 	useCase: 'Find',
	// 	roles: ['Program Management', 'Product', 'Engineering'],
	// },
	{
		id: 'find-relevant-confluence-pages',
		title: 'Find relevant Confluence pages',
		prompt:
			'For `work item ID/project/feature`, find and list any related Confluence pages or reference work items. Include links and a brief summary of how each is relevant.',
		useCase: 'Find',
		roles: ['Program Management', 'Product', 'Engineering'],
	},
	// {
	// 	id: 'find-requirements-jira-work-item',
	// 	title: 'Find requirements for a Jira work item',
	// 	prompt:
	// 		'Identify and document the requirements for work item `work item ID`. Ensure all necessary details are captured, including dependencies and acceptance criteria.',
	// 	useCase: 'Find',
	// 	roles: ['Engineering', 'Product', 'Program Management'],
	// },
	// {
	// 	id: 'find-subject-matter-expertise',
	// 	title: 'Find subject-matter expertise',
	// 	prompt:
	// 		'Find the subject matter expert for `topic`. I need to contact them for `purpose, e.g., advice, review, collaboration`. Confirm the recommended person is the most relevant and available expert.',
	// 	useCase: 'Find',
	// 	roles: ['General', 'Engineering', 'Product', 'Program Management'],
	// },
	// {
	// 	id: 'draft-it-support-response',
	// 	title: 'Draft IT Support response',
	// 	prompt:
	// 		'Draft a reply to a user who is experiencing `topic` issues. Provide clear, empathetic guidance and suggest next steps for resolving the problem using this guide/playbook [Confluence page link]. Here is the user request: `User request`.',
	// 	useCase: 'Create',
	// 	roles: ['Customer Support', 'Helpdesk Support'],
	// },
	{
		id: 'find-latest-company-policies',
		title: 'Find the latest company policies',
		prompt: 'How do I request reimbursements for my travels?',
		useCase: 'Learn',
		roles: ['General'],
	},
	{
		id: 'generate-faq',
		title: 'Generate an FAQ',
		prompt:
			'Map out the typical customer journey for `product/service`, from initial discovery through onboarding, usage, and support. Then, generate top FAQs that customers most frequently ask at each stage—covering how to get started, feature usage, troubleshooting, billing, and best practices—with concise, step-by-step answers formatted for a chatbot.',
		useCase: 'Create',
		roles: ['Customer Support', 'Program Management', 'Product', 'Marketing'],
	},
	{
		id: 'generate-post-project-surveys',
		title: 'Generate post-project surveys',
		prompt:
			"Create a comprehensive evaluation survey for `project` to assess the project's performance, including successes, challenges, areas for improvement, and lessons learned. Include questions such as scale, multiple choice, and open answer covering: objectives and outcomes, budget and resources, team performance and collaboration, challenges and problem-solving, stakeholder satisfaction, and recommendations for future projects.",
		useCase: 'Create',
		roles: ['Program Management', 'Product', 'General'],
	},
	// {
	// 	id: 'identify-upsell-opportunities',
	// 	title: 'Identify upsell opportunities',
	// 	prompt:
	// 		"Analyze the provided call transcript `link to call transcript` with the customer `customer name` and identify all instances where the customer expresses interest in additional features or services. For each instance, include relevant background context, the exact customer quote indicating interest, and a brief explanation of why it represents a potential upsell opportunity. Additionally, recommend the most suitable upsell product or service based on the customer's expressed needs or goals.",
	// 	useCase: 'Analyze',
	// 	roles: ['Sales', 'Product', 'Customer Support', 'Marketing'],
	// },
	{
		id: 'learn-company-procedure-information',
		title: 'Learn about company procedure information',
		prompt:
			'Summarize the standard procedures for `process/activity` across the company. Include step-by-step instructions and reference documents.',
		useCase: 'Summarize',
		roles: ['General', 'IT Ops', 'Program Management'],
	},
	// {
	// 	id: 'plan-sprint-tasks',
	// 	title: 'Plan sprint tasks',
	// 	prompt:
	// 		'Generate a detailed task list for our upcoming sprint using the information provided in `project poster`. For each task, include the task name, a brief description, the responsible team member, and estimated completion time. Organize the tasks by priority level and group them according to their related user stories to ensure clarity and alignment with sprint goals. Flag any missing details needed to finalize sprint planning.',
	// 	useCase: 'Create',
	// 	roles: ['Engineering', 'Program Management', 'Product', 'IT Ops'],
	// },
	// {
	// 	id: 'prepare-performance-review',
	// 	title: 'Prepare for your performance review',
	// 	prompt:
	// 		'Prepare talking points for an upcoming growth conversation with my manager, summarizing my work between `dates`, organized by key projects. The points should facilitate a productive discussion about my performance, achievements, and areas for improvement. They should be clear, concise, and backed by specific examples and evidence. Organize into sections for each major project, including: project overview, my specific contributions, and relevant links.',
	// 	useCase: 'Summarize',
	// 	roles: ['General'],
	// },
	// {
	// 	id: 'retrieve-pull-request-insights-jira',
	// 	title: 'Retrieve pull request insights from Jira',
	// 	prompt:
	// 		'For the given Jira work item Jira work item `link to Jira work item`, retrieve all linked pull requests across repositories. Include each pull request number, title, current review status such as Draft, Open, Approved, Merged, or Rejected, the associated reviewers, and the merge date if applicable. Output a structured table with the columns PR Number, Title, Status, Reviewers, and Merge Date.',
	// 	useCase: 'Find',
	// 	roles: ['Engineering'],
	// },
	// {
	// 	id: 'review-improve-presentation',
	// 	title: 'Review and improve presentation',
	// 	prompt:
	// 		'Review this presentation `upload or link presentation slides/script` and provide me with suggestions on how to improve it. Please consider the following context: the audience consists of [audience, e.g., senior executives, students, clients], the primary goal is [goal, e.g., to persuade, to inform, to inspire], and the desired outcome is `outcome, e.g., increased engagement, clear understanding, actionable next steps`. Focus your feedback on the overall structure, flow, clarity, and impact, and tailor your suggestions to maximize effectiveness for this specific audience and goal.',
	// 	useCase: 'Review',
	// 	roles: ['General'],
	// },
	{
		id: 'review-improve-writing',
		title: 'Review and improve writing',
		prompt:
			'Rewrite the following message to make it more clear, concise, and professional while retaining all key details: `Insert message here`.',
		useCase: 'Review',
		roles: ['General', 'Sales', 'Customer Support'],
	},
	{
		id: 'search-policies-questionnaires',
		title: 'Search policies related to questionnaires',
		prompt:
			'Search all internal security and compliance policies and provide answers to the following questionnaire. Reference policy documents and highlight relevant sections for each answer.',
		useCase: 'Find',
		roles: ['Security', 'IT Ops'],
	},
	// {
	// 	id: 'send-ms-teams-weekly-progress',
	// 	title: 'Send MS Teams message of weekly progress',
	// 	prompt:
	// 		'Draft a summary of the key updates and additions I made over the past week. Send this summary in MS Teams to `Teams channel link`. Confirm the draft with me before sending.',
	// 	useCase: 'Execute',
	// 	roles: ['General'],
	// },
	{
		id: 'suggest-process-streamlining',
		title: 'Suggest process streamlining',
		prompt:
			'Suggest ways to streamline the process for `process/task`. Identify steps that can be automated or simplified to reduce manual work and produce more succinct outputs.',
		useCase: 'Learn',
		roles: ['Program Management', 'IT Ops', 'Engineering'],
	},
	{
		id: 'summarize-track-project-updates',
		title: 'Summarize and track project updates',
		prompt:
			'Summarize the status and history of `project/topic/feature`. Include key initiatives, progress made, stakeholders, decisions, timelines, and relevant documents. Highlight upcoming milestones or blockers.',
		useCase: 'Summarize',
		roles: ['General', 'Engineering', 'Product', 'Program Management'],
	},
	// {
	// 	id: 'summarize-confluence-page-google-doc',
	// 	title: 'Summarize Confluence page and add to Google doc',
	// 	prompt:
	// 		'Summarize key points and any action items/next steps in this Confluence page: `Confluence page link` and add this summary to a new Google doc titled "`title`" under the folder "`folder`."',
	// 	useCase: 'Summarize',
	// 	roles: ['General'],
	// },
	// {
	// 	id: 'summarize-engineering-requests',
	// 	title: 'Summarize engineering requests',
	// 	prompt: 'Review and summarize recent Engineering Requests (ERs) for my team.',
	// 	useCase: 'Review',
	// 	roles: ['Engineering', 'Product', 'Program Management'],
	// },
	{
		id: 'summarize-meeting-notes',
		title: 'Summarize meeting notes',
		prompt:
			'Summarize the meeting with the transcript `attach file or paste transcript`, including main topics discussed, key decisions, and action items.',
		useCase: 'Summarize',
		roles: ['General'],
	},
	{
		id: 'synthesize-user-testing-findings',
		title: 'Synthesize findings from user testing',
		prompt:
			'Synthesize key insights from my user testing: `Confluence page link/transcript`. Identify the top pain points or issues that users faced during the testing. Categorize these issues by severity and impact on user experience.',
		useCase: 'Analyze',
		roles: ['Design'],
	},
	{
		id: 'visualize-data-charts',
		title: 'Visualize your data into charts',
		prompt:
			'`Upload your dataset file(s)` Based on the uploaded dataset file, determine the most effective way to visualize the data. Identify the key structure and variables within the dataset and the most likely user goals for this data, to suggest and describe the most appropriate type of visualization.',
		useCase: 'Analyze',
		roles: ['Product', 'Engineering', 'Marketing'],
	},
	// {
	// 	id: 'create-detailed-retrospective',
	// 	title: 'Create detailed retrospective',
	// 	prompt:
	// 		'Prepare a retrospective page based on the transcript here `document link`. Include the following points: what was done well, what needs improvement, ideas for improvements, and an action plan for the next sprint. Focus on specific project aspects such as `enter aspects description, e.g., team communication, code quality, delivery speed`.',
	// 	useCase: 'Create',
	// 	roles: ['Engineering', 'Product', 'Program Management'],
	// },
	// {
	// 	id: 'write-detailed-runbooks',
	// 	title: 'Write detailed runbooks',
	// 	prompt:
	// 		'Create a detailed, repeatable runbook for this fulfilled service request: `work item link`. Include: a summary, approval steps with conditions, tasks by role/team, internal notes and communications, final provisioning details, and confirmation of fulfillment. List all actions in order with tools/commands used, prerequisites, dependencies, and opportunities for automation or process improvement. Use clear sections, numbered steps, and bullet points for clarity. Include relevant documentation links.',
	// 	useCase: 'Create',
	// 	roles: ['Program Management', 'IT Ops', 'Helpdesk Support'],
	// },
];

export const app3pPrompts: PromptCard[] = [
	// Salesforce
	{
		id: '3p-salesforce-recommend-next-steps-deals',
		title: 'Recommend next steps for my deals',
		prompt: 'Recommend next steps for my deals',
		useCase: 'Execute',
		roles: ['Sales'],
		appIds: ['salesforce'],
	},
	// SharePoint & One Drive & Google Drive
	{
		id: '3p-onboarding-guide',
		title: 'Create onboarding guide',
		prompt: 'Create an onboarding guide for new hires using documents in `product` folder',
		useCase: 'Create',
		roles: ['General', 'Product', 'Program Management'],
		appIds: ['google_drive', 'microsoft_sharepoint', 'microsoft_onedrive'],
	},
	{
		id: '3p-find-product-specs-last-month',
		title: 'Find product specs from last month',
		prompt:
			'Find product specs in `Google Drive/Sharepoint/Onedrive` that were created in the last month',
		useCase: 'Find',
		roles: ['Product', 'Engineering', 'Program Management'],
		appIds: ['google_drive', 'microsoft_sharepoint', 'microsoft_onedrive'],
	},
	{
		id: '3p-callout-risks-fy27',
		title: 'Callout the risks in Strategy doc',
		prompt: 'Callout the risks you see in the FY27 Strategy doc shared with me.',
		useCase: 'Review',
		roles: ['General', 'Product', 'Program Management'],
		appIds: ['google_drive', 'microsoft_sharepoint', 'microsoft_onedrive'],
	},
];

const staticPromptsWithSkill: PromptCard[] = [
	// {
	// 	id: 'summarize-release',
	// 	title: 'Summarize release',
	// 	prompt:
	// 		"'/summarize-release' this `Jira release` for `product` to generate an executive snapshot. Include: 1) key changes grouped by epic or component, 2) risks and blockers with owners and dates, 3) quality signals (open bugs, regressions, flaky tests), 4) planned rollout and key dates, and 5) clear next steps for stakeholders.",
	// 	useCase: 'Summarize',
	// 	roles: ['Product', 'Engineering'],
	// 	enableDeepResearch: true,
	// },
	{
		id: 'create-release',
		title: 'Create release',
		prompt:
			"'/create-release' in this `Jira space` to create a new release with start and release dates. Include completed work items and summarize what's included by epic or team.",
		useCase: 'Create',
		roles: ['Program Management', 'Engineering'],
		tags: ['jira-releases'],
	},
	// {
	// 	id: 'draft-release-notes',
	// 	title: 'Draft release notes',
	// 	prompt:
	// 		'\'/draft-release-notes\' for this `Jira release` and related `Confluence page` to generate customer-facing release notes. Organize into "What\'s new", "Improvements", "Fixes", and "Known issues" with clear, non-technical language.',
	// 	useCase: 'Create',
	// 	roles: ['Marketing', 'Engineering'],
	// 	enableDeepResearch: true,
	// },
	// {
	// 	id: 'create-work-items',
	// 	title: 'Create work items',
	// 	prompt:
	// 		"'/create-work-items' from this `Confluence page` (feature spec) in this `Jira space`. Break the feature into epics, stories, and tasks with summaries, descriptions, and estimates. List everything created, grouped by epic.",
	// 	useCase: 'Create',
	// 	roles: ['Product', 'Engineering'],
	// 	enableDeepResearch: true,
	// },
	// {
	// 	id: 'write-status-update',
	// 	title: 'Write status update',
	// 	prompt:
	// 		"'/write-status-update' with this `Jira space` and these `Confluence pages` to draft a status update for `project/initiative` over the last `time period`. Include overall RAG, key achievements, major risks/blockers with owners, and next steps.",
	// 	useCase: 'Create',
	// 	roles: ['Program Management', 'Product'],
	// 	enableDeepResearch: true,
	// },
	// {
	// 	id: 'page-catchup',
	// 	title: 'Page catchup',
	// 	prompt:
	// 		"'/page-catchup' this `Confluence page` to summarize what changed in the last `time period`. Highlight new sections, major edits, important comments, and open tasks or questions.",
	// 	useCase: 'Summarize',
	// 	roles: ['General'],
	// },
	// {
	// 	id: 'update-work-items',
	// 	title: 'Update work items',
	// 	prompt:
	// 		"'/update-work-items' for these `Jira work items` in this `Jira space` to bulk update: set assignee, add label, and move items to a new status if no blockers. Show a summary of planned changes first.",
	// 	useCase: 'Execute',
	// 	roles: ['Program Management', 'Engineering'],
	// },
	// {
	// 	id: 'manage-jira-permissions',
	// 	title: 'Manage Jira permissions',
	// 	prompt:
	// 		"'/manage-jira-permissions' for this `Jira work item` or `Jira space` to diagnose permission issues, explain missing permissions, and provide admin guidance to resolve.",
	// 	useCase: 'Execute',
	// 	roles: ['IT Ops', 'Security'],
	// },
	// {
	// 	id: 'find-jira-settings',
	// 	title: 'Find Jira settings',
	// 	prompt:
	// 		'\'/find-jira-settings\' for this `Jira space` and my goal "`desired behavior`". Tell me which settings to change, where to find them, and any trade-offs.',
	// 	useCase: 'Find',
	// 	roles: ['IT Ops'],
	// },
	{
		id: 'manage-jira-workflows',
		title: 'Manage Jira workflows',
		prompt:
			"'/manage-jira-workflows' for this `Jira space` to analyze its workflow: explain statuses, transitions, top workflow rules, and propose simplifications.",
		useCase: 'Execute',
		roles: ['IT Ops', 'Program Management'],
		tags: ['jira-workflows'],
	},
	// {
	// 	id: 'search-assets',
	// 	title: 'Search Assets',
	// 	prompt:
	// 		'\'/search-assets\' in this `JSM project` for "`asset search criteria`" and highlight any related to this `JSM incident` or `Jira work item`. Return a table of matching assets.',
	// 	useCase: 'Find',
	// 	roles: ['IT Ops'],
	// },
	// {
	// 	id: 'create-pir',
	// 	title: 'Create PIR',
	// 	prompt:
	// 		"'/create-pir' for this `JSM incident` and related `Confluence pages` to draft a complete Post Incident Review with all standard sections.",
	// 	useCase: 'Create',
	// 	roles: ['IT Ops'],
	// 	enableDeepResearch: true,
	// },
	{
		id: 'analyze-incident-root-cause',
		title: 'Find root cause of an incident (RCA)',
		prompt:
			"'/analyze-incident-root-cause' for this `JSM incident` to analyze details, linked alerts, changes, assets, and similar incidents. Propose likely root cause and investigation steps.",
		useCase: 'Analyze',
		roles: ['IT Ops'],
		enableDeepResearch: true,
	},
	{
		id: 'find-incident-root-cause',
		title: 'Find incident root cause',
		prompt:
			"'/find-incident-root-cause' for this `JSM incident` to infer the most likely root cause and provide a stakeholder-friendly summary.",
		useCase: 'Find',
		roles: ['IT Ops', 'General'],
		enableDeepResearch: true,
	},
	// {
	// 	id: 'find-incidents',
	// 	title: 'Find incidents',
	// 	prompt:
	// 		'\'/find-incidents\' in this `JSM project` for incidents from the last `time period` matching "`criteria`". Group by status or service, show counts, and surface the most impactful incidents.',
	// 	useCase: 'Find',
	// 	roles: ['IT Ops'],
	// },
	{
		id: 'find-similar-incidents',
		title: 'Find similar incidents',
		prompt:
			"'/find-similar-incidents' for this `JSM incident` to find the most similar historical incidents, how they were resolved, and any recurring patterns.",
		useCase: 'Find',
		roles: ['IT Ops'],
		enableDeepResearch: true,
	},
	{
		id: 'suggest-problem-ticket',
		title: 'Suggest problem ticket',
		prompt:
			"'/suggest-problem-ticket' for this `JSM incident` to check for similar incidents and, if recurring, propose a problem ticket with all key details and links.",
		useCase: 'Create',
		roles: ['IT Ops'],
	},
	{
		id: 'suggest-incident-fields',
		title: 'Suggest incident fields',
		prompt:
			"'/suggest-incident-fields' for this `JSM incident` to propose values for service, component, priority, impact, urgency, and major incident flag, with justifications.",
		useCase: 'Execute',
		roles: ['Helpdesk Support', 'IT Ops'],
	},
	{
		id: 'suggest-incident-sme',
		title: 'Suggest incident SME',
		prompt:
			"'/suggest-incident-expert' for this `JSM incident` to recommend the best people or teams to involve, with reasons and suggested actions.",
		useCase: 'Find',
		roles: ['IT Ops'],
	},
	{
		id: 'summarize-incident',
		title: 'Summarize incident',
		prompt:
			"'/summarize-incident' for this `JSM incident` to create an executive summary with impact, timeline, root cause, mitigation, and next steps.",
		useCase: 'Summarize',
		roles: ['IT Ops', 'General'],
	},
	// {
	// 	id: 'find-alerts',
	// 	title: 'Find alerts',
	// 	prompt:
	// 		"'/find-alerts' in this `JSM project` for alerts from the last `time period`. Group by service and severity, highlight recurring alerts, and relate to incidents or changes.",
	// 	useCase: 'Find',
	// 	roles: ['IT Ops', 'Engineering'],
	// },
	// {
	// 	id: 'recommend-playbook',
	// 	title: 'Recommend playbook',
	// 	prompt:
	// 		"'/recommend-playbook' for this `alert` or `JSM incident` to suggest the best runbooks or playbooks, with links and first steps.",
	// 	useCase: 'Find',
	// 	roles: ['IT Ops', 'Engineering'],
	// },
	{
		id: 'assess-change-risk-insights',
		title: 'Assess change risk insights',
		prompt:
			"'/assess-change-risk' for this `JSM change request` to assess risk, explain drivers, and suggest safeguards like canary deployments or extra approvals.",
		useCase: 'Analyze',
		roles: ['IT Ops'],
	},
	{
		id: 'get-data-insights',
		title: 'Get data insights',
		prompt:
			"'/get-data-insights' for this `Jira space` to create charts (throughput, cycle time, WIP) and interpret trends, bottlenecks, and recommendations.",
		useCase: 'Analyze',
		roles: ['Engineering', 'Program Management'],
		enableDeepResearch: true,
	},
	// {
	// 	id: 'summarize-focus-area',
	// 	title: 'Summarize focus area',
	// 	prompt:
	// 		"'/summarize-focus-area' for this `focus area` in Goals to produce a snapshot for `time period`. Summarize status, achievements, risks/blockers, and next steps for executive updates.",
	// 	useCase: 'Summarize',
	// 	roles: ['General', 'Program Management'],
	// 	enableDeepResearch: true,
	// },
];

const staticPromptsWithSkillRefactored: PromptCard[] = [
	{
		id: 'create-release',
		title: 'Create release',
		prompt:
			"/create-release in this `Jira space` to create a new release with start and release dates. Include completed work items and summarize what's included by epic or team.",
		useCase: 'Create',
		roles: ['Program Management', 'Engineering'],
		tags: ['jira-releases'],
	},
	{
		id: 'manage-jira-workflows',
		title: 'Manage Jira workflows',
		prompt:
			'/manage-jira-workflows for this `Jira space` to analyze its workflow: explain statuses, transitions, top workflow rules, and propose simplifications.',
		useCase: 'Execute',
		roles: ['IT Ops', 'Program Management'],
		tags: ['jira-workflows'],
	},
	{
		id: 'analyze-incident-root-cause',
		title: 'Find root cause of an incident (RCA)',
		prompt:
			'/analyze-incident-root-cause for this `JSM incident` to analyze details, linked alerts, changes, assets, and similar incidents. Propose likely root cause and investigation steps.',
		useCase: 'Analyze',
		roles: ['IT Ops'],
		enableDeepResearch: true,
	},
	{
		id: 'find-incident-root-cause',
		title: 'Find incident root cause',
		prompt:
			'/find-incident-root-cause for this `JSM incident` to infer the most likely root cause and provide a stakeholder-friendly summary.',
		useCase: 'Find',
		roles: ['IT Ops', 'General'],
		enableDeepResearch: true,
	},

	{
		id: 'find-similar-incidents',
		title: 'Find similar incidents',
		prompt:
			'/find-similar-incidents for this `JSM incident` to find the most similar historical incidents, how they were resolved, and any recurring patterns.',
		useCase: 'Find',
		roles: ['IT Ops'],
		enableDeepResearch: true,
	},
	{
		id: 'suggest-problem-ticket',
		title: 'Suggest problem ticket',
		prompt:
			'/suggest-problem-ticket for this `JSM incident` to check for similar incidents and, if recurring, propose a problem ticket with all key details and links.',
		useCase: 'Create',
		roles: ['IT Ops'],
	},
	{
		id: 'suggest-incident-fields',
		title: 'Suggest incident fields',
		prompt:
			'/suggest-incident-fields for this `JSM incident` to propose values for service, component, priority, impact, urgency, and major incident flag, with justifications.',
		useCase: 'Execute',
		roles: ['Helpdesk Support', 'IT Ops'],
	},
	{
		id: 'suggest-incident-sme',
		title: 'Suggest incident SME',
		prompt:
			'/suggest-incident-sme for this `JSM incident` to recommend the best people or teams to involve, with reasons and suggested actions.',
		useCase: 'Find',
		roles: ['IT Ops'],
	},
	{
		id: 'summarize-incident',
		title: 'Summarize incident',
		prompt:
			'/summarize-incident for this `JSM incident` to create an executive summary with impact, timeline, root cause, mitigation, and next steps.',
		useCase: 'Summarize',
		roles: ['IT Ops', 'General'],
	},
	{
		id: 'assess-change-risk-insights',
		title: 'Assess change risk insights',
		prompt:
			'/show-changes for this `JSM change request` to assess risk, explain drivers, and suggest safeguards like canary deployments or extra approvals.',
		useCase: 'Analyze',
		roles: ['IT Ops'],
	},
	{
		id: 'get-data-insights',
		title: 'Get data insights',
		prompt:
			'/get-data-insights for this `Jira space` to create charts (throughput, cycle time, WIP) and interpret trends, bottlenecks, and recommendations.',
		useCase: 'Analyze',
		roles: ['Engineering', 'Program Management'],
		enableDeepResearch: true,
	},
];

// Filters out prompt cards that reference skills but have no skill metadata.
// This will also act as an entitlement check for the user.
const filterCardsBySkillMetadata = (
	cards: PromptCard[],
	skillMetadataMap: Record<string, unknown> | undefined,
): PromptCard[] => {
	if (skillMetadataMap === undefined) {
		return cards;
	}
	return cards.filter((card) => {
		const slugs = getSkillSlugsInPrompt(card.prompt);
		if (slugs.length === 0) return true;
		return slugs.every((slug) => slug in skillMetadataMap);
	});
};

// Hook to allow users to create custom prompts in the future
export const usePromptCards = (): {
	promptCards: PromptCard[];
} => {
	const skillMetadata = useSkillMetadata();
	const enableSkillsUiM1 = fg('rovo_chat_enable_skills_ui_m1');
	// First 3 curated prompts, then skills prompts, then the rest
	const first3Prompts = staticPrompts.slice(0, 3);
	const remainingPrompts = staticPrompts.slice(3);
	const skillsPrompts = filterCardsBySkillMetadata(
		enableSkillsUiM1 && fg('rovo_chat_encode_adf_with_skills')
			? staticPromptsWithSkillRefactored
			: enableSkillsUiM1 && fg('rovo_skills_prompt_library')
			? staticPromptsWithSkill
			: [],
		skillMetadata?.skillMetadataMap,
	);
	const promptCards = [...first3Prompts, ...skillsPrompts, ...remainingPrompts];
	return {
		promptCards,
	};
};
